// Colour utilities for the Citizen Charter colour studio.
// HEX <-> RGB <-> HSL conversions + harmonious palette generation from a primary colour.

export function hexToRgb(hex) {
  let h = String(hex || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map(c => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return { r: 0, g: 0, b: 0 };
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}

export function rgbToHex({ r, g, b }) {
  const p = v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${p(r)}${p(g)}${p(b)}`.toUpperCase();
}

export function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hslToRgb({ h, s, l }) {
  h = ((h % 360) + 360) % 360; s = Math.max(0, Math.min(100, s)) / 100; l = Math.max(0, Math.min(100, l)) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
}

export const hexToHsl = (hex) => rgbToHsl(hexToRgb(hex));
export const hslToHex = (hsl) => rgbToHex(hslToRgb(hsl));

// [r,g,b] array for pdf-lib
export const hexToRgbArr = (hex) => { const { r, g, b } = hexToRgb(hex); return [r, g, b]; };

export const PALETTE_SLOTS = [
  { key: "primary", label: "Primary" },
  { key: "secondary", label: "Secondary" },
  { key: "accent", label: "Accent" },
  { key: "heading", label: "Headings" },
  { key: "background", label: "Page Background" },
  { key: "table_header", label: "Table Header" },
  { key: "highlight", label: "Highlight / Alt Rows" },
  { key: "border", label: "Borders" },
  { key: "text", label: "Body Text" },
  { key: "muted", label: "Muted Text" },
];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Build a harmonious full palette from a single primary colour.
// text/muted stay readable neutrals; everything else derives from the primary hue.
export function harmonyFromPrimary(primaryHex) {
  const { h, s, l } = hexToHsl(primaryHex);
  const S = clamp(s, 25, 80);
  return {
    primary: rgbToHex(hslToRgb({ h, s: S, l: clamp(l, 22, 45) })),
    secondary: hslToHex({ h, s: clamp(S - 8, 18, 75), l: clamp(l - 13, 9, 32) }),
    accent: hslToHex({ h: (h + 42) % 360, s: clamp(S + 5, 35, 80), l: 45 }),
    heading: hslToHex({ h, s: clamp(S - 5, 20, 75), l: clamp(l - 16, 8, 30) }),
    background: hslToHex({ h, s: clamp(S - 40, 12, 45), l: 96 }),
    table_header: rgbToHex(hslToRgb({ h, s: S, l: clamp(l, 22, 45) })),
    highlight: hslToHex({ h, s: clamp(S - 35, 14, 45), l: 92 }),
    border: hslToHex({ h, s: clamp(S - 45, 12, 40), l: 82 }),
    text: "#1F2937",
    muted: "#6B7280",
  };
}
